// Picks one representative colour out of an image (e.g. an avatar) for ambient tinting: the image
// is downsampled to 32×32, pixels are bucketed by hue/saturation/lightness and weighted toward
// mid-lightness, saturated ones, and the heaviest bucket wins — falling back to the heaviest
// neutral bucket for greyscale images. The result is normalised into a readable saturation/
// lightness range and returned as a CSS `rgb(r g b)` string. Results are cached per URL.
//
// The image is read through a canvas, so it must be same-origin or served with CORS headers;
// otherwise the canvas is tainted and the promise rejects.

type Hsl = {
  h: number
  s: number
  l: number
}

type ColorCluster = {
  r: number
  g: number
  b: number
  weight: number
}

const SAMPLE_SIZE = 32
const colorCache = new Map<string, Promise<string>>()

const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value))
}

const rgbToHsl = (r: number, g: number, b: number): Hsl => {
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const lightness = (max + min) / 2
  const delta = max - min

  if (delta === 0) {
    return { h: 0, s: 0, l: lightness }
  }

  const saturation = lightness > 0.5
    ? delta / (2 - max - min)
    : delta / (max + min)
  let hue: number

  if (max === r) {
    hue = ((g - b) / delta + (g < b ? 6 : 0)) * 60
  } else if (max === g) {
    hue = ((b - r) / delta + 2) * 60
  } else {
    hue = ((r - g) / delta + 4) * 60
  }

  return { h: hue, s: saturation, l: lightness }
}

const hslToRgb = ({ h, s, l }: Hsl): { r: number, g: number, b: number } => {
  const chroma = (1 - Math.abs(2 * l - 1)) * s
  const hueSection = h / 60
  const secondary = chroma * (1 - Math.abs(hueSection % 2 - 1))
  let red = 0
  let green = 0
  let blue = 0

  if (hueSection < 1) {
    red = chroma
    green = secondary
  } else if (hueSection < 2) {
    red = secondary
    green = chroma
  } else if (hueSection < 3) {
    green = chroma
    blue = secondary
  } else if (hueSection < 4) {
    green = secondary
    blue = chroma
  } else if (hueSection < 5) {
    red = secondary
    blue = chroma
  } else {
    red = chroma
    blue = secondary
  }

  const offset = l - chroma / 2

  return {
    r: Math.round((red + offset) * 255),
    g: Math.round((green + offset) * 255),
    b: Math.round((blue + offset) * 255),
  }
}

const addToCluster = (
  clusters: Map<string, ColorCluster>,
  key: string,
  r: number,
  g: number,
  b: number,
  weight: number,
) => {
  const cluster = clusters.get(key) ?? { r: 0, g: 0, b: 0, weight: 0 }

  cluster.r += r * weight
  cluster.g += g * weight
  cluster.b += b * weight
  cluster.weight += weight
  clusters.set(key, cluster)
}

const selectColor = (clusters: Map<string, ColorCluster>): string | null => {
  let selected: ColorCluster | null = null

  for (const cluster of clusters.values()) {
    if (!selected || cluster.weight > selected.weight) {
      selected = cluster
    }
  }

  if (!selected) {
    return null
  }

  const red = selected.r / selected.weight
  const green = selected.g / selected.weight
  const blue = selected.b / selected.weight
  const selectedHsl = rgbToHsl(red / 255, green / 255, blue / 255)
  const normalizedHsl = {
    h: selectedHsl.h,
    s: selectedHsl.s < 0.1 ? selectedHsl.s : clamp(selectedHsl.s * 1.15, 0.48, 0.9),
    l: clamp(selectedHsl.l, 0.43, 0.65),
  }
  const normalizedColor = hslToRgb(normalizedHsl)

  return `rgb(${normalizedColor.r} ${normalizedColor.g} ${normalizedColor.b})`
}

const extractRepresentativeColor = async (src: string): Promise<string> => {
  const image = new Image()
  image.crossOrigin = 'anonymous'

  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve()
    image.onerror = () => reject(new Error(`Unable to load image: ${src}`))
    image.src = src
  })

  const canvas = document.createElement('canvas')
  canvas.width = SAMPLE_SIZE
  canvas.height = SAMPLE_SIZE

  const context = canvas.getContext('2d', { willReadFrequently: true })

  if (!context) {
    throw new Error('Canvas 2D context is unavailable')
  }

  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(image, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE)

  const pixels = context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data
  const coloredClusters = new Map<string, ColorCluster>()
  const neutralClusters = new Map<string, ColorCluster>()

  for (let index = 0; index < pixels.length; index += 4) {
    const alpha = pixels[index + 3]! / 255

    if (alpha < 0.5) {
      continue
    }

    const red = pixels[index]!
    const green = pixels[index + 1]!
    const blue = pixels[index + 2]!
    const { h, s, l } = rgbToHsl(red / 255, green / 255, blue / 255)

    if (l < 0.1 || l > 0.92) {
      continue
    }

    const lightnessWeight = 0.1 + Math.sin(Math.PI * l) ** 2
    const saturationWeight = 0.25 + s ** 1.35
    const weight = alpha * lightnessWeight * saturationWeight
    const neutralKey = `${Math.floor(l * 6)}`
    addToCluster(neutralClusters, neutralKey, red, green, blue, weight)

    if (s >= 0.14) {
      const colorKey = `${Math.floor(h / 15)}:${Math.floor(s * 4)}:${Math.floor(l * 4)}`
      addToCluster(coloredClusters, colorKey, red, green, blue, weight)
    }
  }

  const color = selectColor(coloredClusters) ?? selectColor(neutralClusters)

  if (!color) {
    throw new Error('Image contains no usable pixels')
  }

  return color
}

export const getRepresentativeColor = (src: string): Promise<string> => {
  const cachedColor = colorCache.get(src)

  if (cachedColor) {
    return cachedColor
  }

  const colorPromise = extractRepresentativeColor(src).catch(error => {
    colorCache.delete(src)
    throw error
  })

  colorCache.set(src, colorPromise)
  return colorPromise
}
